import { useCallback, useLayoutEffect, useMemo, useRef, useState, type UIEvent } from "react";
import { isToday } from "date-fns";
import { ArrowLeftRight, CalendarDays, Check, ChevronLeft, ChevronRight, Minus, Pencil, Repeat, UserX, X } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { Calendar } from "@/modern/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { cn } from "@/modern/utils";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useBoardUsers } from "@/react/hooks/useBoardUsers";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { toast } from "sonner";
import type { CheckGridCellHistoryEntry, CheckGridCellPatch, CheckGridDefinition, CheckGridMatrix, CheckGridMatrixCell, CheckGridOverviewItem, CheckGridTemplateItem, CheckGridSubject } from "./types";
import { UNASSIGNED_SUBJECT_ID } from "./types";
import { DISPLAY_STATE_LABEL, cellCornerDotClass, cellNeedsAttention, cellStateClass, overviewSignal, recurrenceLabel, signalMeta } from "./utils/display";
import { formatDayLabel, shiftDay } from "./utils/dayWindow";
import { completeTogglePatch } from "./utils/cellPatch";
import { CheckgridMenu, type CheckgridMenuAction } from "./CheckgridMenu";
import { CheckgridEntityLink } from "./CheckgridEntityLink";
import { CheckgridReviewPanel, type CheckgridReviewGroup } from "./CheckgridReviewPanel";
import { CheckgridHeaderProgress } from "./CheckgridProgressMini";

type Props = {
  grid: CheckGridOverviewItem;
  definition: CheckGridDefinition | null;
  matrix: CheckGridMatrix | null;
  day: Date;
  /** Board-calendar day the grid was created; earlier days can't be picked. */
  firstDay: Date | null;
  onDayChange: (day: Date) => void;
  onAction: (action: CheckgridMenuAction, grid: CheckGridOverviewItem, extras?: { file?: File }) => void;
  onPatchCell: (checklistItemId: number, body: CheckGridCellPatch) => Promise<void>;
  onLoadHistory: (checklistItemId: number, boardUserId?: number) => Promise<CheckGridCellHistoryEntry[]>;
  canEdit: boolean;
  isSaving?: boolean;
};

function cellKey(subjectId: number, itemId: number) {
  return `${subjectId}:${itemId}`;
}

function entityId(entity: CheckGridSubject | CheckGridTemplateItem): number {
  return "description" in entity ? entity.id : entity.subject_id;
}

function isCheckItem(entity: CheckGridSubject | CheckGridTemplateItem): entity is CheckGridTemplateItem {
  return "description" in entity;
}

function entityLabel(entity: CheckGridSubject | CheckGridTemplateItem): string {
  return "description" in entity ? entity.description : entity.name;
}

function TypeTag({ children, className }: { children: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "pointer-events-none shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10.5px] leading-none font-medium tracking-wide whitespace-nowrap text-muted-foreground uppercase animate-in fade-in duration-200 motion-reduce:animate-none",
        className
      )}
    >
      {children}
    </span>
  );
}

/** In-cell mark pending — soft ping + orbit, direction-aware check or dot. */
function CellPendingMark({ checking }: { checking: boolean }) {
  return (
    <span className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden rounded-md" aria-hidden>
      <span
        className={cn(
          "absolute inset-0 animate-in fade-in duration-150 motion-reduce:animate-none",
          checking ? "bg-success/25" : "bg-foreground/[0.06]"
        )}
      />
      <span
        className={cn(
          "absolute size-3 rounded-full opacity-50 animate-ping motion-reduce:animate-none",
          checking ? "bg-success" : "bg-muted-foreground/70"
        )}
      />
      <span className="relative flex size-6 items-center justify-center">
        <span
          className={cn(
            "absolute inset-0 rounded-full border-2 border-transparent animate-spin motion-reduce:animate-none",
            checking ? "border-t-success-foreground border-r-success-foreground/35" : "border-t-foreground/55 border-r-foreground/20"
          )}
          style={{ animationDuration: "0.65s" }}
        />
        {checking ? (
          <Check className="size-3.5 text-success-foreground animate-in zoom-in-50 fade-in duration-200 motion-reduce:animate-none" strokeWidth={3} />
        ) : (
          <span className="size-1.5 rounded-full bg-foreground/55 animate-pulse motion-reduce:animate-none" />
        )}
      </span>
    </span>
  );
}

const REVIEW_MODIFIER = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent) ? "Option" : "Alt";

const LABEL_W_MIN = 224;
const LABEL_W_MAX = 480;
const CELL_W = 76;
/** Keep a little matrix viewport even when labels expand into spare width. */
const MATRIX_VIEWPORT_MIN = 160;
/** Right padding + room for the hover type tag beside a row name. */
const LABEL_CONTENT_PAD = 72;
/** Minimum column-header band for rotated labels. */
const HEADER_H_MIN = 132;
/** Cap so a long label can’t dominate the pane; still grows when content needs it. */
const HEADER_H_MAX = 360;
/** Top type-tag pad + bottom breathing room inside the column header. */
const HEADER_LABEL_PAD = 32;
const ROW_H = 60;

type MatrixFreezePaneProps = {
  rows: Array<CheckGridSubject | CheckGridTemplateItem>;
  cols: Array<CheckGridSubject | CheckGridTemplateItem>;
  cellMap: Map<string, CheckGridMatrixCell>;
  idsAt: (rowId: number, colId: number) => { subjectId: number; itemId: number };
  hoverRow: number | null;
  hoverCol: number | null;
  hoverAt: (rowId: number | null, colId: number | null) => void;
  isHovering: boolean;
  rowTypeName: string;
  colTypeName: string;
  onLineClick: (event: React.MouseEvent, axis: "row" | "col", id: number) => void;
  onCellClick: (event: React.MouseEvent, subjectId: number, itemId: number) => void;
  reviewSet: Set<string>;
  hintKey: string | null;
  /** Whether this subject’s cells can be ticked (edit grant or self). */
  canTickSubject: (subjectId: number) => boolean;
  isPast: boolean;
  pending: { key: string; checking: boolean } | null;
  /** Current board-user id when they appear as a matrix subject. */
  mySubjectId: number | undefined;
};

function MatrixFreezePane({
  rows,
  cols,
  cellMap,
  idsAt,
  hoverRow,
  hoverCol,
  hoverAt,
  isHovering,
  rowTypeName,
  colTypeName,
  onLineClick,
  onCellClick,
  reviewSet,
  hintKey,
  canTickSubject,
  isPast,
  pending,
  mySubjectId
}: MatrixFreezePaneProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const labelScrollRef = useRef<HTMLDivElement>(null);
  const matrixScrollRef = useRef<HTMLDivElement>(null);
  const syncingRef = useRef(false);
  const colHeaderRef = useRef<HTMLDivElement>(null);
  const [headerH, setHeaderH] = useState(HEADER_H_MIN);
  const [labelW, setLabelW] = useState(LABEL_W_MIN);
  const labelMaxH = Math.max(72, headerH - HEADER_LABEL_PAD);
  const rowCount = rows.length;
  const colCount = cols.length;
  const colLabelsKey = cols.map((c) => entityLabel(c)).join("\0");
  const rowLabelsKey = rows.map((r) => entityLabel(r)).join("\0");

  // Size header to longest column label; widen row labels to longest name. Cap by viewport so the grid stays top-aligned.
  useLayoutEffect(() => {
    const el = rootRef.current;
    const labelPane = labelScrollRef.current;
    const colHeader = colHeaderRef.current;
    if (!el) return;
    const update = () => {
      const viewportCap = Math.max(HEADER_H_MIN, el.clientHeight - rowCount * ROW_H);
      let colNeeded = HEADER_H_MIN;
      if (colHeader) {
        for (const node of Array.from(colHeader.querySelectorAll<HTMLElement>("[data-col-label]"))) {
          const prev = node.style.maxHeight;
          node.style.maxHeight = "none";
          // vertical-rl: full label length may land on scrollHeight or scrollWidth depending on engine
          colNeeded = Math.max(colNeeded, Math.ceil(Math.max(node.scrollHeight, node.scrollWidth)) + HEADER_LABEL_PAD);
          node.style.maxHeight = prev;
        }
      }
      setHeaderH(Math.min(HEADER_H_MAX, viewportCap, Math.max(HEADER_H_MIN, colNeeded)));

      const matrixContentW = colCount * CELL_W;
      const widthCap = Math.max(LABEL_W_MIN, el.clientWidth - Math.max(matrixContentW, MATRIX_VIEWPORT_MIN));
      let needed = LABEL_W_MIN;
      if (labelPane) {
        for (const node of Array.from(labelPane.querySelectorAll<HTMLElement>("[data-row-label]"))) {
          needed = Math.max(needed, node.scrollWidth + LABEL_CONTENT_PAD);
        }
      }
      setLabelW(Math.min(LABEL_W_MAX, widthCap, needed));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [rowCount, colCount, colLabelsKey, rowLabelsKey]);

  const syncScroll = useCallback((source: "label" | "matrix") => {
    if (syncingRef.current) return;
    const labels = labelScrollRef.current;
    const matrixEl = matrixScrollRef.current;
    if (!labels || !matrixEl) return;
    syncingRef.current = true;
    if (source === "matrix") labels.scrollTop = matrixEl.scrollTop;
    else matrixEl.scrollTop = labels.scrollTop;
    syncingRef.current = false;
  }, []);

  const onLabelScroll = (event: UIEvent<HTMLDivElement>) => {
    event.stopPropagation();
    syncScroll("label");
  };
  const onMatrixScroll = () => syncScroll("matrix");

  return (
    <div ref={rootRef} className="flex min-h-0 min-w-0 flex-1 overflow-hidden bg-background">
      <div
        className="flex shrink-0 flex-col bg-background transition-[width] duration-200 ease-out motion-reduce:transition-none"
        style={{ width: labelW }}
      >
        <div className="shrink-0 bg-background" style={{ height: headerH }} />
        <div
          ref={labelScrollRef}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-background [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          onScroll={onLabelScroll}
        >
          {rows.map((row) => {
            const rowId = entityId(row);
            const rowLabel = entityLabel(row);
            const rowIsCheck = isCheckItem(row);
            // Only person labels — never check-item rows (idsAt would wrongly use the first column’s subject).
            const isMineRow = !rowIsCheck && mySubjectId != null && rowId === mySubjectId;
            return (
              <div
                key={rowId}
                className="flex items-center bg-background py-2 pr-4"
                style={{ height: ROW_H }}
                onMouseEnter={() => hoverAt(rowId, null)}
              >
                <div className="flex min-w-0 items-center gap-2" style={{ width: labelW - 20 }}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        data-row-label
                        onClick={(event) => onLineClick(event, "row", rowId)}
                        className={cn(
                          "min-w-0 truncate rounded-sm text-left text-[13.5px] transition-opacity duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none",
                          // People: strong foreground; checks: muted — matches matrix reference hierarchy.
                          rowIsCheck
                            ? "font-normal text-muted-foreground"
                            : rowId === UNASSIGNED_SUBJECT_ID
                              ? "font-normal text-muted-foreground"
                              : isMineRow
                              ? "font-semibold text-foreground"
                              : "font-medium text-foreground",
                          isHovering && hoverRow !== rowId && "opacity-50"
                        )}
                        aria-current={isMineRow ? "true" : undefined}
                      >
                        {rowLabel}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{isMineRow ? `You · ${rowLabel}` : rowLabel}</TooltipContent>
                  </Tooltip>
                  {hoverRow === rowId && <TypeTag className="slide-in-from-left-1">{rowTypeName}</TypeTag>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div ref={matrixScrollRef} className="min-w-0 flex-1 overflow-auto bg-background" onScroll={onMatrixScroll}>
        <div className="w-max">
          <div ref={colHeaderRef} className="sticky top-0 z-10 flex bg-background" style={{ height: headerH }}>
            {cols.map((c) => {
              const colId = entityId(c);
              const colLabel = entityLabel(c);
              const colIsCheck = isCheckItem(c);
              const isMineCol = !colIsCheck && mySubjectId != null && colId === mySubjectId;
              return (
                <div
                  key={colId}
                  className="relative flex h-full flex-col items-center justify-end bg-background pb-1"
                  style={{ width: CELL_W }}
                  onMouseEnter={() => hoverAt(null, colId)}
                >
                  {/* Sit just above the vertical label — not at the top of a tall header band. */}
                  {hoverCol === colId && <TypeTag className="mb-1.5 shrink-0 slide-in-from-bottom-1">{colTypeName}</TypeTag>}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        data-col-label
                        onClick={(event) => onLineClick(event, "col", colId)}
                        className={cn(
                          "truncate rounded-sm text-[12.5px] leading-none whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          colIsCheck
                            ? hoverCol === colId
                              ? "font-medium text-muted-foreground"
                              : "font-normal text-muted-foreground/80"
                            : colId === UNASSIGNED_SUBJECT_ID
                              ? "font-normal text-muted-foreground"
                              : isMineCol
                              ? "font-semibold text-foreground"
                              : hoverCol === colId
                                ? "font-medium text-foreground"
                                : "font-medium text-foreground/90"
                        )}
                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", maxHeight: labelMaxH }}
                        aria-current={isMineCol ? "true" : undefined}
                      >
                        {colLabel}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" sideOffset={8}>
                      {isMineCol ? `You · ${colLabel}` : colLabel}
                    </TooltipContent>
                  </Tooltip>
                </div>
              );
            })}
          </div>

          {rows.map((row) => {
            const rowId = entityId(row);
            const rowLabel = entityLabel(row);
            return (
              <div key={rowId} className="flex" style={{ height: ROW_H }}>
                {cols.map((col, colIdx) => {
                  const colId = entityId(col);
                  const { subjectId, itemId } = idsAt(rowId, colId);
                  const cell = cellMap.get(cellKey(subjectId, itemId));
                  const state = cell?.display_state ?? "incomplete";
                  const attention = cell?.attention;
                  const inReview = reviewSet.has(cellKey(subjectId, itemId));
                  const colLabel = entityLabel(cols[colIdx]);
                  const corner = state === "satisfied" ? (cell?.has_evidence ? cellCornerDotClass(state, attention) : null) : cellCornerDotClass(state, attention);
                  const isOpen = (state === "incomplete" || state === "expected") && !cellNeedsAttention(state, attention);
                  const key = cellKey(subjectId, itemId);
                  const isHint = hintKey === key;
                  const isPending = pending?.key === key;
                  const isMissed = state === "missed";
                  return (
                    <div key={`${rowId}-${colId}`} className="flex items-center px-1.5" style={{ width: CELL_W }}>
                      <button
                        type="button"
                        onClick={(event) => onCellClick(event, subjectId, itemId)}
                        onMouseEnter={() => hoverAt(rowId, colId)}
                        onFocus={() => hoverAt(rowId, colId)}
                        disabled={isPending}
                        aria-busy={isPending || undefined}
                        aria-disabled={isMissed || undefined}
                        title={`${rowLabel} · ${colLabel} — ${DISPLAY_STATE_LABEL[state]}`}
                        aria-label={
                          isPending
                            ? `${rowLabel}, ${colLabel}: ${pending.checking ? "Marking complete" : "Marking incomplete"}`
                            : `${rowLabel}, ${colLabel}: ${DISPLAY_STATE_LABEL[state]}`
                        }
                        className={cn(
                          "group/cell relative mx-auto flex h-11 w-full items-center justify-center rounded-md transition-[filter,transform,opacity] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring active:scale-95 motion-reduce:transition-none motion-reduce:active:scale-100",
                          cellStateClass(state, attention),
                          isHovering && hoverRow !== rowId && hoverCol !== colId && !isPending && "opacity-35",
                          inReview && "ring-2 ring-primary ring-offset-2 ring-offset-background",
                          isPending && "scale-[0.98] ring-2 ring-primary/35 ring-offset-1 ring-offset-background",
                          isMissed ? "cursor-not-allowed active:scale-100" : !isPending && "hover:brightness-110"
                        )}
                      >
                        {!isPending && state === "satisfied" && <Check className="size-5 text-success-foreground" strokeWidth={3} aria-hidden />}
                        {!isPending && state === "missed" && <X className="size-4" strokeWidth={2.5} aria-hidden />}
                        {!isPending && state === "not_applicable" && <Minus className="size-4 text-muted-foreground/70" aria-hidden />}
                        {!isPending && isOpen && canTickSubject(subjectId) && !isPast && (
                          <Check
                            className="size-5 text-muted-foreground/60 opacity-0 transition-opacity duration-150 group-hover/cell:opacity-100 group-focus-visible/cell:opacity-100"
                            strokeWidth={2.5}
                            aria-hidden
                          />
                        )}
                        {isPending && <CellPendingMark checking={pending.checking} />}
                        {corner && !isPending && <span className={corner} aria-hidden />}
                        {isHint && !isPending && <span className="pointer-events-none absolute -inset-[3px] rounded-[9px] ring-2 ring-primary/60 animate-pulse motion-reduce:animate-none" aria-hidden />}
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function CheckgridDetail({ grid, definition, matrix, day, firstDay, onDayChange, onAction, onPatchCell, onLoadHistory, canEdit, isSaving }: Props) {
  const items = matrix?.items ?? definition?.items ?? [];
  const subjects = matrix?.subjects ?? definition?.subjects ?? [];
  const { userPreferences } = useSwivuBryntumContext();

  const { myBoardUserId } = useBoardUsers();
  const mySubjectId = myBoardUserId();

  const [itemsAsRows, setItemsAsRows] = useState(() => {
    const saved = userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.checkgridRowAxis);
    if (saved === "items" || saved === "subjects") return saved === "items";
    return items.length > subjects.length * 2;
  });
  const toggleRowAxis = () => {
    const next = !itemsAsRows;
    setItemsAsRows(next);
    userPreferences?.updateSetting(PERSISTENCE_STATE_KEYS.checkgridRowAxis, next ? "items" : "subjects");
  };
  const [reviewKeys, setReviewKeys] = useState<string[]>([]);
  const [hoverCol, setHoverCol] = useState<number | null>(null);
  const [hoverRow, setHoverRow] = useState<number | null>(null);
  const [hintDismissed, setHintDismissed] = useState(false);
  const [pending, setPending] = useState<{ key: string; checking: boolean } | null>(null);

  const cellMap = useMemo(() => {
    const map = new Map<string, CheckGridMatrixCell>();
    for (const cell of matrix?.cells ?? []) {
      map.set(cellKey(cell.subject_id, cell.item_id), cell);
    }
    return map;
  }, [matrix]);

  const rows: Array<CheckGridSubject | CheckGridTemplateItem> = itemsAsRows ? items : subjects;
  const cols: Array<CheckGridSubject | CheckGridTemplateItem> = itemsAsRows ? subjects : items;

  const idsAt = (rowId: number, colId: number) => (itemsAsRows ? { subjectId: colId, itemId: rowId } : { subjectId: rowId, itemId: colId });

  const windowGrid = matrix ? { ...grid, summary: matrix.summary } : grid;
  const signal = overviewSignal(windowGrid);
  const meta = signalMeta(signal);
  const isRecurring = grid.recurrence !== "none";
  const hasNoAssignees = subjects.length > 0 && subjects.every((subject) => subject.subject_id === UNASSIGNED_SUBJECT_ID);
  const isPast = matrix?.phase === "past";
  const dayIsToday = isToday(day);
  const dayIsFirst = !!firstDay && day <= firstDay;

  const canTickSubject = useCallback(
    (subjectId: number) => {
      // Unassigned column ticks the caller (no board_user_id) — editors or any signed-in board user.
      if (subjectId === UNASSIGNED_SUBJECT_ID) return Boolean(canEdit || mySubjectId != null);
      return canEdit || (mySubjectId != null && subjectId === mySubjectId);
    },
    [canEdit, mySubjectId]
  );

  const { complete, applicable } = (() => {
    const summary = matrix?.summary ?? grid.summary;
    const app =
      summary.applicable > 0 ? summary.applicable : Math.max(summary.subject_count * summary.item_count, 0);
    return { complete: Math.max(app - summary.unresolved, 0), applicable: app };
  })();

  const progressPct = applicable > 0 ? Math.round((complete / applicable) * 100) : 0;
  const allNotScheduled =
    Boolean(matrix?.cells.length) && matrix!.cells.every((c) => c.display_state === "not_applicable");

  const hintKey = (() => {
    if (hintDismissed || isPast || complete > 0) return null;
    for (const row of rows) {
      for (const col of cols) {
        const { subjectId, itemId } = idsAt(entityId(row), entityId(col));
        if (!canTickSubject(subjectId)) continue;
        const cell = cellMap.get(cellKey(subjectId, itemId));
        if (cell && cell.display_state === "incomplete" && !cellNeedsAttention(cell.display_state, cell.attention)) {
          return cellKey(subjectId, itemId);
        }
      }
    }
    return null;
  })();

  // Matrix subjects are board users — not the checklist’s host entity type.
  const rowTypeName = itemsAsRows ? "Check" : "User";
  const colTypeName = itemsAsRows ? "User" : "Check";
  const isHovering = hoverRow !== null || hoverCol !== null;
  const hoverAt = (rowId: number | null, colId: number | null) => {
    setHoverRow(rowId);
    setHoverCol(colId);
  };

  const reviewSet = new Set(reviewKeys);

  const runPatch = async (checklistItemId: number, body: CheckGridCellPatch, key: string, checking: boolean) => {
    setPending({ key, checking });
    try {
      await onPatchCell(checklistItemId, body);
    } finally {
      setPending((current) => (current?.key === key ? null : current));
    }
  };

  const onCellClick = (event: React.MouseEvent, subjectId: number, itemId: number) => {
    const key = cellKey(subjectId, itemId);
    if (cellMap.get(key)?.display_state === "missed") return;
    setHintDismissed(true);
    if (event.altKey) {
      setReviewKeys((base) => (base.includes(key) ? base.filter((k) => k !== key) : [...base, key]));
      return;
    }
    if (isPast || pending || isSaving) return;
    const cell = cellMap.get(key);
    if (cell?.display_state === "not_applicable") {
      toast.message("Not scheduled", { description: "This check isn’t required for this period." });
      return;
    }
    if (!canTickSubject(subjectId)) return;
    const checklistItemId = cell?.checklist_item_id;
    if (!checklistItemId) {
      toast.error("Unable to update this check.");
      return;
    }
    const done = cell?.display_state === "satisfied";
    void runPatch(checklistItemId, completeTogglePatch(done, subjectId), key, !done);
  };
  const onLineClick = (event: React.MouseEvent, axis: "row" | "col", id: number) => {
    const keys = lineKeys(axis, id);
    setHintDismissed(true);
    if (!event.altKey) {
      setReviewKeys(keys);
      return;
    }
    setReviewKeys((base) => {
      const allIn = keys.every((k) => base.includes(k));
      return allIn ? base.filter((k) => !keys.includes(k)) : [...base, ...keys.filter((k) => !base.includes(k))];
    });
  };
  const lineKeys = (axis: "row" | "col", id: number) =>
    (axis === "row" ? cols : rows).map((other) => {
      const { subjectId, itemId } = axis === "row" ? idsAt(id, entityId(other)) : idsAt(entityId(other), id);
      return cellKey(subjectId, itemId);
    });
  const clearSelection = () => {
    setReviewKeys([]);
  };

  const reviewGroups: CheckgridReviewGroup[] = reviewKeys.length
    ? rows.flatMap((row) => {
        const rowId = entityId(row);
        const entries = cols.flatMap((col) => {
          const { subjectId, itemId } = idsAt(rowId, entityId(col));
          const key = cellKey(subjectId, itemId);
          if (!reviewSet.has(key)) return [];
          const cell = cellMap.get(key);
          const checklistItemId = cell?.checklist_item_id;
          const item = items.find((candidate) => candidate.id === itemId);
          return [
            {
              key,
              label: entityLabel(col),
              itemId,
              itemLabel: item?.description ?? "",
              cell,
              comment: item?.comment,
              evidence: item?.image_url ? { thumbnail: item.thumbnail_url ?? item.image_url, full: item.image_url } : null,
              loadHistory: checklistItemId ? () => onLoadHistory(checklistItemId, subjectId === UNASSIGNED_SUBJECT_ID ? undefined : subjectId) : undefined
            }
          ];
        });
        if (!entries.length) return [];
        return [
          {
            id: rowId,
            label: entityLabel(row),
            entries
          }
        ];
      })
    : [];

  const swapLabel = itemsAsRows ? "Show users as rows" : "Show checks as rows";
  const dayLabel = formatDayLabel(day, true);

  return (
    <div className="flex h-full min-w-0 bg-background">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="border-b border-border px-8 py-5">
          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-1.5">
                <h1 className="min-w-0 truncate text-[20px] tracking-tight text-foreground">{grid.name}</h1>
                {canEdit && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 shrink-0 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                        onClick={() => onAction("edit", grid)}
                        aria-label={`Edit ${grid.name}`}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Edit checklist</TooltipContent>
                  </Tooltip>
                )}
                {isRecurring && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted/70 px-2 py-0.5 text-[11.5px] text-muted-foreground">
                    <Repeat className="size-3" aria-hidden />
                    {recurrenceLabel(grid.recurrence)}
                  </span>
                )}
              </div>
              <CheckgridEntityLink grid={grid} onAction={onAction} className="mt-0.5 text-[13px] text-muted-foreground" />
              <CheckgridHeaderProgress
                complete={complete}
                applicable={applicable}
                percent={progressPct}
                barClassName={meta.bar}
              />
              {hasNoAssignees && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="mt-2.5 flex w-fit shrink-0 items-center gap-1.5 rounded-full bg-warning/15 px-2.5 py-1 text-[12px] font-medium text-warning">
                      <UserX className="size-3.5" aria-hidden />
                      No one assigned
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[280px] text-xs">
                    Nobody is assigned to this checklist. Checks are recorded under whoever ticks them.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            <div className="flex min-w-0 shrink-0 items-center gap-2">
              {isRecurring && (
                <div className="mr-1 flex items-center">
                  {!dayIsToday && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mr-1 h-8 px-2.5 text-[12.5px] text-muted-foreground animate-in fade-in slide-in-from-right-1 duration-150 hover:text-foreground motion-reduce:animate-none"
                      onClick={() => onDayChange(new Date())}
                    >
                      Back to today
                    </Button>
                  )}
                  <div className="flex items-center rounded-lg bg-muted/60 p-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-7 rounded-md text-muted-foreground hover:bg-background hover:text-foreground"
                          onClick={() => onDayChange(shiftDay(day, -1))}
                          disabled={dayIsFirst}
                          aria-label="Previous day"
                        >
                          <ChevronLeft className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">Previous day</TooltipContent>
                    </Tooltip>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex h-7 min-w-[132px] items-center justify-center gap-1.5 rounded-md px-2.5 text-[13px] text-foreground transition-colors duration-150 hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label="Pick a date"
                        >
                          <CalendarDays className="size-3.5 text-muted-foreground" aria-hidden />
                          <span key={dayLabel} className="animate-in fade-in duration-150 motion-reduce:animate-none">
                            {dayLabel}
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar mode="single" selected={day} onSelect={(d) => d && onDayChange(d)} disabled={firstDay ? [{ before: firstDay }, { after: new Date() }] : { after: new Date() }} initialFocus />
                      </PopoverContent>
                    </Popover>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-7 rounded-md text-muted-foreground hover:bg-background hover:text-foreground"
                          onClick={() => onDayChange(shiftDay(day, 1))}
                          disabled={dayIsToday}
                          aria-label="Next day"
                        >
                          <ChevronRight className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">Next day</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={toggleRowAxis}
                    aria-label={swapLabel}
                  >
                    <ArrowLeftRight className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{swapLabel}</TooltipContent>
              </Tooltip>
              <CheckgridMenu grid={grid} onAction={onAction} size="md" />
            </div>
          </div>
        </header>

        <div
          key={matrix?.from ?? "empty"}
          className="flex min-h-0 flex-1 flex-col px-8 py-6 animate-in fade-in duration-150 motion-reduce:animate-none"
          onMouseLeave={() => hoverAt(null, null)}
          onKeyDown={(event) => {
            if (event.key === "Escape") clearSelection();
          }}
        >
          {!matrix || !subjects.length || !items.length ? (
            <p className="text-[13.5px] text-muted-foreground">No subjects or items on this grid for this window.</p>
          ) : allNotScheduled ? (
            <p className="text-[13.5px] text-muted-foreground">
              {dayIsToday ? "Nothing scheduled today." : "Nothing scheduled for this day."}
            </p>
          ) : (
            // Split panes — names never share a scrollport with cells, so horizontal scroll cannot paint under labels.
            <MatrixFreezePane
              rows={rows}
              cols={cols}
              cellMap={cellMap}
              idsAt={idsAt}
              hoverRow={hoverRow}
              hoverCol={hoverCol}
              hoverAt={hoverAt}
              isHovering={isHovering}
              rowTypeName={rowTypeName}
              colTypeName={colTypeName}
              onLineClick={onLineClick}
              onCellClick={onCellClick}
              reviewSet={reviewSet}
              hintKey={hintKey}
              canTickSubject={canTickSubject}
              isPast={isPast}
              pending={pending}
              mySubjectId={mySubjectId}
            />
          )}
        </div>
        {matrix && rows.length * cols.length > 1 && (
          <p
            className={cn(
              "border-t border-border px-8 py-2.5 text-[12px] text-muted-foreground/60 transition-opacity duration-150 motion-reduce:transition-none",
              isHovering ? "opacity-100" : "opacity-0"
            )}
          >
            Click a cell to mark it complete or incomplete. Click a name to review its row or column. Hold {REVIEW_MODIFIER} and click to multi-select.
          </p>
        )}
      </div>

      {reviewGroups.length > 0 && (
        <CheckgridReviewPanel
          gridName={grid.name}
          dayLabel={formatDayLabel(day)}
          groups={reviewGroups}
          definition={definition}
          onClose={clearSelection}
        />
      )}
    </div>
  );
}
